import React from 'react';
import { get } from 'lodash';
import { Button, Space } from 'antd';
import { ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { humanize } from '@utils';
import UnitToggle from '@components/common/UnitToggle';
import LinkTrends from './LinkTrends';
import { checkFeature } from '@utils/platform/features/checkFeature';
import Cell from './Cell';
import { getMetricMetaByKey, getMetricByUnit } from '@utils/metrics';

const ROW_HEIGHT = 45;
const HEADER_HEIGHT = 45;
const MAX_HEIGHT = 290;

export const normalisePercentage = (value) => {
  value = Number(value);
  if (value && value < 0.001) {
    return '< 0.001';
  }

  return value;
};

export const humanizeHoc = (value, unit) => {
  let humanizedVal = humanize(value, unit);
  if (unit === '%') {
    humanizedVal = normalisePercentage(humanizedVal);
  }

  return humanizedVal;
};

function buildColumns({
  metrics,
  unit,
  flowId,
  frustrationSignalId,
  excludedMetricTypes,
  handleUnitChange,
  handleExpandChange,
}) {
  const columns: any = [
    {
      title: 'Filter',
      key: 'filter',
      dataKey: 'filter',
      width: 200,
      align: 'left',
      frozen: true,
      resizable: true,
      dataGetter: ({ rowData }) => {
        return rowData.filter.name;
      },
    },
  ];

  const quickCompareDrilldown = checkFeature('trends.base.quick-compare-drilldown', true);

  const getMetricByUnitToggle = (metric, unit) => {
    return getMetricByUnit(unit, metric);
  };

  // generate columns based on metrics
  // skip # metrics if a % metric exists
  metrics.forEach((metricKey) => {
    const actualMetricKey = getMetricByUnitToggle(metricKey, unit) || metricKey;
    const metricsMeta = getMetricMetaByKey({
      metricKey: actualMetricKey,
      flowOverride: !!flowId,
      frustrationSignalOverride: !!frustrationSignalId,
    });
    columns.push({
      title: metricsMeta?.short || actualMetricKey,
      key: actualMetricKey,
      dataKey: actualMetricKey,
      width: 100,
      align: 'left',
      dataGetter: (data) => {
        const { column, rowData, rowIndex } = data;
        const { dataKey } = column;
        if (dataKey in rowData) {
          const metricType = get(metricsMeta, [dataKey, 'type'], '');
          const isUserDefined = get(metricsMeta, [dataKey, 'category'], []).includes(
            'ECO_USER_DEFINED_METRICS',
          );

          // Skip display rule for user defined metrics to adapt dummy dimension changes
          if (excludedMetricTypes[rowIndex]?.includes(metricType) && !isUserDefined) {
            return 'NA';
          }

          if (['NA', 'low'].includes(rowData[dataKey])) {
            return rowData[dataKey];
          }

          const unit = get(metricsMeta, 'unit', '');
          const value = humanizeHoc(rowData[dataKey], unit);

          return unit !== '' ? `${value} ${unit}` : value;
        }
        return '';
      },
      cellRenderer: ({ cellData, rowData, column, container }) => {
        return <Cell cellData={cellData} rowData={rowData} column={column} container={container} />;
      },
      frozen: false,
    });
  });

  // open a new drill-down dashboard
  columns.push({
    key: 'Action',
    title: 'Action',
    width: 200,
    align: 'center',
    frozen: 'right',
    headerRenderer: ({ container }) => {
      const selectedUnit = get(container, ['props', 'headerProps', 'unit'], '#');
      const expandState = get(container, ['props', 'headerProps', 'expand'], false);
      return (
        <Space>
          <Button
            style={{ fontSize: 18, lineHeight: 1 }}
            icon={expandState ? <ShrinkOutlined /> : <ExpandAltOutlined />}
            onClick={handleExpandChange}
          />
          <UnitToggle selectedUnit={selectedUnit} onChange={handleUnitChange} />
        </Space>
      );
    },
    cellRenderer: ({ rowData }) => {
      return quickCompareDrilldown && <LinkTrends rowData={rowData} metrics={metrics} />;
    },
  });

  return columns;
}

function calcHeight(rowLen = 0, expand = false) {
  const reqSize = 10 + HEADER_HEIGHT + ROW_HEIGHT * rowLen;
  return expand ? reqSize : Math.min(MAX_HEIGHT, reqSize);
}

export { buildColumns, calcHeight, HEADER_HEIGHT, ROW_HEIGHT };
